import { Component, ViewEncapsulation, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PageTitleComponent } from '../../../../componentes/page-title/page-title.component';
import { ListadoAplicacionesComponent } from './perfiles/listado-aplicaciones.component';
import { ListadoEncendidosComponent } from './encendidos/listado-encendidos.component';

type Vista = 'perfiles' | 'encendidos';

/**
 * Seguimiento de luminarias: cómo viene lo que se mandó.
 *
 * Un **perfil** se manda de a pocas luminarias por vez, así que su avance se
 * mide sobre la configuración que tiene que quedar en cada una. Un **encendido
 * o apagado** sale de una vez hacia todas, y su avance es cuántas lo aplicaron.
 *
 * La solapa va en la ruta (`/perfiles`, `/encendidos`) y no en un query param:
 * así el "volver" de cada detalle cae en su solapa. `?hijos=0` limita al
 * cliente propio y las solapas lo conservan.
 */
@Component({
  selector: 'app-seguimiento-luminarias',
  standalone: true,
  imports: [
    MatIconModule,
    MatTabsModule,
    RouterLink,
    PageTitleComponent,
    ListadoAplicacionesComponent,
    ListadoEncendidosComponent,
  ],
  template: `
    <div class="seg">
      <app-page-title
        title="Seguimiento"
        subtitle="Cómo viene lo que se mandó a las luminarias: cuántas ya quedaron con cada perfil y cuántas aplicaron cada encendido o apagado."
        icon="track_changes"
        color="gray"
        padding="16px 16px 8px 16px"
      ></app-page-title>

      <nav
        mat-tab-nav-bar
        [tabPanel]="panel"
        class="seg-solapas"
        aria-label="Qué seguir"
      >
        <a
          mat-tab-link
          routerLink="/main/luminarias/seguimiento/perfiles"
          [queryParams]="{ hijos: hijos() ? null : '0' }"
          [active]="vista() === 'perfiles'"
        >
          <mat-icon class="seg-solapas__icono">tune</mat-icon>
          Perfiles
        </a>
        <a
          mat-tab-link
          routerLink="/main/luminarias/seguimiento/encendidos"
          [queryParams]="{ hijos: hijos() ? null : '0' }"
          [active]="vista() === 'encendidos'"
        >
          <mat-icon class="seg-solapas__icono">lightbulb</mat-icon>
          Encendido y apagado
        </a>
      </nav>
      <mat-tab-nav-panel #panel>
        <div class="seg-contenido">
          @if (vista() === 'encendidos') {
            <app-listado-encendidos />
          } @else {
            <app-listado-aplicaciones />
          }
        </div>
      </mat-tab-nav-panel>
    </div>
  `,
  styleUrl: './seguimiento.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SeguimientoLuminariasComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly datos = toSignal(this.route.data, { initialValue: {} });
  private readonly query = toSignal(this.route.queryParamMap, {
    initialValue: undefined,
  });

  protected readonly vista = computed<Vista>(() =>
    (this.datos() as { vista?: Vista }).vista === 'encendidos'
      ? 'encendidos'
      : 'perfiles',
  );

  protected readonly hijos = computed(() => this.query()?.get('hijos') !== '0');
}
